import React, { useState, useEffect, useRef } from 'react';
import './List.css';
import { MdVerified } from "react-icons/md";
import { VscUnverified } from "react-icons/vsc";
import { MdOutlineInfo } from "react-icons/md";
import { TbArrowBackUp } from "react-icons/tb";
import AddressDisplay from '../addressdisplay/AddressDisplay';
import Tweet from '../../assets/tweet.png';

const Verified = MdVerified  as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Unverified = VscUnverified as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Info = MdOutlineInfo as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const Back = TbArrowBackUp as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const ListProject: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({
    question: '',
    answer: 0,
    userAnswer: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const submitTimeRef = useRef<number>(Date.now());

  const [formData, setFormData] = useState({
    contractAddress: '',
    chain: '',
    projectName: '',
    projectSymbol: '',
    projectDescription: '',
    projectImage: '',
    bannerImage: '',
    website: '',
    twitter: '',
    telegram: '',
    telegramUsername: '',
    projectStage: '',
    hasAudit: false,
    auditUrl: '',
    hasKyc: false,
    kycUrl: '',
    wantsVerified: false,
    hashUrl: '',
    presaleUrl: '',
    enddate: ''
  });

  // Generate simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ['+', '-', '*'];
    const operator = operators[Math.floor(Math.random() * operators.length)];
    
    let answer: number;
    let question: string;
    
    switch (operator) {
      case '+':
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case '-':
        // Ensure positive result
        const larger = Math.max(num1, num2);
        const smaller = Math.min(num1, num2);
        answer = larger - smaller;
        question = `${larger} - ${smaller}`;
        break;
      case '*':
        answer = num1 * num2;
        question = `${num1} × ${num2}`;
        break;
      default:
        answer = num1 + num2;
        question = `${num1} + ${num2}`;
    }
    
    setCaptcha({
      question,
      answer,
      userAnswer: ''
    });
  };

  // Initialize captcha on component mount
  useEffect(() => {
    generateCaptcha();
    submitTimeRef.current = Date.now();
  }, []);

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCaptchaChange = (e: any) => {
    setCaptcha(prev => ({
      ...prev,
      userAnswer: e.target.value
    }));
  };

  const validateAntiBot = (): string | null => {
    // Check honeypot (should be empty)
    if (honeypot.trim() !== '') {
      return 'Bot detected. Please try again.';
    }

    // Check captcha
    if (parseInt(captcha.userAnswer) !== captcha.answer) {
      return 'Please solve the math problem correctly.';
    }

    // Check minimum time spent on form (at least 10 seconds)
    const timeSpent = Date.now() - submitTimeRef.current;
    if (timeSpent < 10000) {
      return 'Please take more time to fill out the form.';
    }

    return null;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return; // Prevent double submission
    }

    // Anti-bot validation
    const botError = validateAntiBot();
    if (botError) {
      alert(botError);
      generateCaptcha(); // Generate new captcha
      return;
    }

    setIsSubmitting(true);
    
    // Validate required fields
    const requiredFields = [
      { field: 'contractAddress' as keyof typeof formData, label: 'Contract Address' },
      { field: 'chain' as keyof typeof formData, label: 'Chain' },
      { field: 'projectName' as keyof typeof formData, label: 'Project Name' },
      { field: 'projectSymbol' as keyof typeof formData, label: 'Project Symbol' },
      { field: 'projectDescription' as keyof typeof formData, label: 'Project Description' },
      { field: 'twitter' as keyof typeof formData, label: 'twitter' },
      { field: 'projectStage' as keyof typeof formData, label: 'Project Stage' }
    ];

    const missingFields = [];
    
    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        missingFields.push(label);
      }
    }

    // Check conditional required fields
    if (formData.hasAudit && (!formData.auditUrl || formData.auditUrl.trim() === '')) {
      missingFields.push('Audit URL');
    }
    
    if (formData.hasKyc && (!formData.kycUrl || formData.kycUrl.trim() === '')) {
      missingFields.push('KYC URL');
    }

    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields:\n\n${missingFields.join('\n')}`);
      setIsSubmitting(false);
      return;
    }
    
    try {
      const message = `*New Project Submission:*

*Project Name:* \`${formData.projectName}\`
*Project Symbol:* \`${formData.projectSymbol}\`
*Chain:* \`${formData.chain}\`
*Contract Address:* \`${formData.contractAddress}\`
*Project Stage:* \`${formData.projectStage}\`
*Description:* \`${formData.projectDescription}\`

*Presale URL:* \`${formData.presaleUrl}\`
*Presale End date:* \`${formData.enddate}\`

*Images:*
*Project Image:* \`${formData.projectImage}\`
*Banner Image:* \`${formData.bannerImage || 'Not provided'}\`

*Social Links:*
*Website:* \`${formData.website || 'Not provided'}\`
*Telegram:* \`${formData.telegram || 'Not provided'}\`
*Twitter/X:* \`${formData.twitter || 'Not provided'}\`

*Contact:*
*Telegram Username:* \`${formData.telegramUsername}\`

*Wants verified:* \`${formData.wantsVerified ? 'Yes' : 'No'}\`${formData.wantsVerified ? `
*Hash:* \`${formData.hashUrl}\`` : ''}

*Security:*
*Audit Completed:* \`${formData.hasAudit ? 'Yes' : 'No'}\`${formData.hasAudit ? `
*Audit URL:* \`${formData.auditUrl}\`` : ''}
*KYC Completed:* \`${formData.hasKyc ? 'Yes' : 'No'}\`${formData.hasKyc ? `
*KYC URL:* \`${formData.kycUrl}\`` : ''}`;

      const response = await fetch('https://tgpushmessage.replit.app/api/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          parse_mode: "MarkdownV2"
        })
      });

      if (response.ok) {
        alert('Project submitted successfully!');
        // Reset form
        setFormData({
          contractAddress: '',
          chain: '',
          projectName: '',
          projectSymbol: '',
          projectDescription: '',
          projectImage: '',
          bannerImage: '',
          website: '',
          twitter: '',
          telegram: '',
          telegramUsername: '',
          projectStage: '',
          hasAudit: false,
          auditUrl: '',
          hasKyc: false,
          kycUrl: '',
          wantsVerified: false,
          hashUrl: '',
          presaleUrl: '',
          enddate: ''
        });
        setHoneypot('');
        generateCaptcha();
        submitTimeRef.current = Date.now();
      } else {
        alert('Failed to submit project. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred while submitting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='listform'>
      <div className="listform-inner">
          <div className="listform-row">

            {/* Chain */}
            <span>
              <label htmlFor="chain">
                Chain *
              </label>
              <select
                id="chain"
                name="chain"
                value={formData.chain}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="">Select Chain</option>
                <option value="ethereum">Ethereum</option>
                <option value="bsc">Binance Smart Chain</option>
                <option value="polygon">Polygon</option>
                <option value="arbitrum">Arbitrum</option>
                <option value="optimism">Optimism</option>
                <option value="avalanche">Avalanche</option>
                <option value="solana">Solana</option>
                <option value="base">Base</option>
                <option value="pepeunchained">Pepe Unchained</option>
                <option value="robin">Robinhood Chain</option>
                <option value="other">Other</option>
              </select>
            </span>

            <div>
              <label htmlFor="contractAddress">
                Contract Address *
              </label>
              <input
                type="text"
                id="contractAddress"
                name="contractAddress"
                value={formData.contractAddress}
                onChange={handleInputChange}
                required
                placeholder="0x..."
                disabled={isSubmitting}
              />
            </div>

          </div>

          {/* Honeypot field (hidden from users) */}
          <div style={{ display: 'none' }}>
            <label htmlFor="website_url">Website URL (leave empty)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Project Name */}
          <div className="listform-row">
            <div>
              <label htmlFor="projectName">
                Project Name *
              </label>
              <input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                required
                placeholder="Enter project name"
                disabled={isSubmitting}
              />
            </div>

            {/* Project Symbol */}
            <div>
              <label htmlFor="projectSymbol">
                Project Symbol *
              </label>
              <input
                type="text"
                id="projectSymbol"
                name="projectSymbol"
                value={formData.projectSymbol}
                onChange={handleInputChange}
                required
                placeholder="e.g., BTC, ETH"
                disabled={isSubmitting}
              />
            </div>

          {/* Project Stage */}
          <div>
            <label htmlFor="projectStage">
              Project Stage *
            </label>
            <select
              id="projectStage"
              name="projectStage"
              value={formData.projectStage}
              onChange={handleInputChange}
              required
              className='projectstage'
              disabled={isSubmitting}
            >
              <option value="">Select Stage</option>
              <option value="pre-launch">Pre-launch</option>
              <option value="presale">Presale</option>
              <option value="launched">Launched</option>
              <option value="pumpfun">Pump.fun</option>
              <option value="fourmeme">Four.meme</option>
              <option value="other">Other</option>
            </select>
          </div>
          </div>

            {formData.projectStage === 'presale' && (
              <div className='expanddiv'>
                <div className="listform-row">
                  <div>
                    <label htmlFor="presaleUrl">
                      Presale URL
                    </label>
                    <input
                      type="url"
                      id="presaleUrl"
                      name="presaleUrl"
                      value={formData.presaleUrl}
                      onChange={handleInputChange}
                      placeholder="https://..."
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="presaleUrl">
                      Presale Enddate
                    </label>
                    <input
                      type="datetime-local"
                      id="enddate"
                      name="enddate"
                      value={formData.enddate}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            )}

          {/* Project Description */}
          <div>
            <label htmlFor="projectDescription">
              Project Description *
            </label>
            <textarea
              id="projectDescription"
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleInputChange}
              required
              rows={2}
              placeholder="Describe your project..."
              disabled={isSubmitting}
            />
          </div>
          {/*
          <div className="listform-row">

            
            <div>
              <label htmlFor="projectImage">
                Project Image *
              </label>
              <input
                type="url"
                id="projectImage"
                name="projectImage"
                value={formData.projectImage}
                onChange={handleInputChange}
                required
                placeholder="https://example.com/logo.png"
                disabled={isSubmitting}
              />
              <p className='listformnote'>Recommended size: 500 x 500 pixels</p>

            </div>

          
            <div>
              <label htmlFor="bannerImage">
                Banner Image (optional)
              </label>
              <input
                type="url"
                id="bannerImage"
                name="bannerImage"
                value={formData.bannerImage}
                onChange={handleInputChange}
                required
                placeholder="https://example.com/banner.png"
                disabled={isSubmitting}
              />
              <p className='listformnote'>Recommended size: 1000 x 333 pixels</p>
            </div>
          </div>
          */}
          {/* Website */}
          <div className="listform-row">
            <div>
              <label htmlFor="website">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://yourproject.com"
                disabled={isSubmitting}
              />
            </div>

            {/* Twitter/X */}
            <div>
              <label htmlFor="twitter">
                X (Twitter) *
              </label>
              <input
                type="url"
                id="twitter"
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                placeholder="https://x.com/yourproject"
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Telegram */}
            <div>
              <label htmlFor="telegram">
                Telegram
              </label>
              <input
                type="url"
                id="telegram"
                name="telegram"
                value={formData.telegram}
                onChange={handleInputChange}
                placeholder="https://t.me/yourproject"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Telegram Username */}
          <div>
            <label htmlFor="telegramUsername">
              Your Telegram Username
            </label>
            <input
              type="text"
              id="telegramUsername"
              name="telegramUsername"
              value={formData.telegramUsername}
              onChange={handleInputChange}
              required
              placeholder="@yourusername"
              disabled={isSubmitting}
            />
          </div>

          {/* Audit */}
          <div className="listform-row">
          <div>
            <div className='checkbox-item'>
              <input
                type="checkbox"
                id="hasAudit"
                name="hasAudit"
                checked={formData.hasAudit}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
              <label htmlFor="hasAudit">
                Project completed audit (optional)
              </label>
            </div>
            
            {formData.hasAudit && (
              <div className='expanddiv'>
                <label htmlFor="auditUrl">
                  Audit URL *
                </label>
                <input
                  type="url"
                  id="auditUrl"
                  name="auditUrl"
                  value={formData.auditUrl}
                  onChange={handleInputChange}
                  required={formData.hasAudit}
                  placeholder="https://audit-report-url.com"
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>

          {/* KYC */}
          <div>
            <div className='checkbox-item'>
              <input
                type="checkbox"
                id="hasKyc"
                name="hasKyc"
                checked={formData.hasKyc}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
              <label htmlFor="hasKyc">
                Project completed KYC (optional)
              </label>
            </div>
            
            {formData.hasKyc && (
              <div className='expanddiv'>
                <label htmlFor="kycUrl">
                  KYC URL *
                </label>
                <input
                  type="url"
                  id="kycUrl"
                  name="kycUrl"
                  value={formData.kycUrl}
                  onChange={handleInputChange}
                  required={formData.hasKyc}
                  placeholder="https://kyc-verification-url.com"
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>
          </div>

            <div className='checkbox-item verifiedcheck'>
              <input
                type="checkbox"
                id="wantsVerified"
                name="wantsVerified"
                checked={formData.wantsVerified}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
              <label htmlFor="wantsVerified" className='getverifiedinput'>
                I want to obtain the <div className="risk-amount verified smallerverified"><Verified /> Verified</div> badge (optional)
              </label>
            </div>
            <p className='listformnote verifiednote'>Recommended for more exposure</p>

            {formData.wantsVerified && (
              <div className='expanddiv'>
                <b>For a verified listing on Coinsult please send €150 worth of crypto to one of the following addresses:</b>
                <span><AddressDisplay address='0xd63bfd0e912ab250a4ac5528d888ab20acca95ec' /> (ETH, BSC, MATIC)</span>
                <span><AddressDisplay address='AujMMoGDaKLb2fKJmmUUg5Vc3tWxnZioQugA9GcSnZLa' /> (SOL)</span>
                <span><AddressDisplay address='TAyEkP75JRa9zZZ4bxRiLcZtsYPTsAdvfi' /> (TRC20)</span>
                <label className='paymenthash' htmlFor="hashUrl">
                  Verified Payment Hash
                </label>
                <input
                  type="text"
                  id="hashUrl"
                  name="hashUrl"
                  value={formData.hashUrl}
                  onChange={handleInputChange}
                  required={formData.wantsVerified}
                  placeholder="0x..."
                  disabled={isSubmitting}
                />
              </div>
            )}

          {/* Anti-bot Captcha */}
          <div className='captchadiv'>
              <label htmlFor="captcha">
                Security Check: What is {captcha.question}? *
              </label>
              <input
                type="number"
                id="captcha"
                name="captcha"
                value={captcha.userAnswer}
                onChange={handleCaptchaChange}
                required
                placeholder="Enter the answer"
                disabled={isSubmitting}
              />
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="button"
              onClick={handleSubmit}
              className='submitproject'
              disabled={isSubmitting}
              style={{
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                position: 'relative'
              }}
            >
              {isSubmitting ? (
                <>
                  <span style={{ marginRight: '8px' }}>⏳</span>
                  Submitting...
                </>
              ) : (
                'Submit Project'
              )}
            </button>
          </div>
      </div>

      <div className="verifiedright">
        <div className='benefitsver'>
          <div className="title-box-listing">
            Free vs Verified
          </div>
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="Recently listed (10,000+ daily users)">
                <div className="risk-amount low smallerverified"><> Free</></div>
            </div>
            <div>
              Recently listed (10,000+ daily users)
            </div>
          </div>
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="Dedicated Project Page">
                <div className="risk-amount low smallerverified"><> Free</></div>
            </div>
            <div>
              Dedicated Project Page
            </div>
          </div>
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="TG Listing Post (10k+ Members)">
                <div className="risk-amount low smallerverified"><> Free</></div>
            </div>
            <div>
              TG Listing Post (10k+ Members)
            </div>
          </div>              
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="Verified Trending Bar">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              Verified Trending Bar 🔥
            </div>
          </div>              
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="X Post (40k+ Followers)">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              X Post (40k+ Followers) 🎁
            </div>
          </div>              
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="Giveaway $20 (Included)">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              Giveaway $20 (Included)
            </div>
          </div>              
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="Verified badge">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              Verified badge 
            </div>
          </div>
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="Trending boost">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              Trending boost 🚀
            </div>
          </div>
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="All projects section priority">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              All projects section priority
            </div>
          </div>
          <div className="listing-item-options">
            <div data-tooltip-id="global-tooltip" data-tooltip-content="All projects section priority">
              <div className="risk-amount medium smallerverified"><> Verified</></div>
            </div>
            <div className='verifiedoption'>
              Free changes to your project page
            </div>
          </div>
        </div>

        <div className="tweetpicture">
          <div className="title-box-listing">
            Free Exposure on X
          </div>
          <img src={Tweet}></img>
        </div>
      </div>
    </div>
  );
}

export default ListProject;